<template>
  <div data-component-group="threads-form" data-component="labels">
    <slot name="beforeForm" />

    <v-autocomplete
      v-model="selectedLabels"
      :items="combinedLabels"
      multiple
      chips
      :closable-chips="!isDisabled"
      :hide-details="false"
      :loading="isLoadingLabels"
      prepend-inner-icon="label"
      label="Labels"
      item-title="name"
      item-value="id"
      return-object
      :readonly="isDisabled"
      :menu-props="{ nudgeWidth: -120 }"
      v-model:search="labelSearch"
    />

    <div v-if="!isDisabled" class="component-actions">
      <v-btn color="secondary" :loading="isLoading" @click="onUpdateLabels">
        <v-icon>edit</v-icon>
        {{ triggerLabel }}
      </v-btn>
    </div>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import { isStr, isObj, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    props: {
      thread: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Update',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        labelSearch: '',
        labels: [],
        selectedLabels: [],
        isLoadingLabels: false,
        isRefreshingLabels: true,
      };
    },
    computed: {
      combinedLabels() {
        const { labels = [], selectedLabels = [] } = this;
        const selectedIds = new Set(selectedLabels.map((l) => l.id));

        return [...selectedLabels, ...labels.filter((l) => !selectedIds.has(l.id))];
      },
    },
    watch: {
      thread(nextThread) {
        if (isObj(nextThread)) {
          this.clear();
          this.setLabels(nextThread.labels);
        }
      },
      label() {
        this.isRefreshingLabels = true;
      },
      labelSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingLabels) {
            this.isRefreshingLabels = false;
            return false;
          }

          debouncer(() => this.searchLabels(nextSearch));
        }
      },
    },
    created() {
      const { thread } = this;

      if (isObj(thread)) {
        this.setLabels(thread.labels);
      }

      this.searchLabels('');
    },
    methods: {
      setLabels(labels) {
        this.labels = labels;
        this.selectedLabels = labels;
      },
      onUpdateLabels() {
        const { selectedLabels, onSubmit } = this;

        onSubmit({
          labels: selectedLabels.map(({ id }) => id),
        });
      },
      searchLabels(search = '') {
        const query = {
          search,
        };

        this.isLoadingLabels = true;
        return this.$store.dispatch('labels/getItems', query).then((res) => {
          this.labels = res.items;
          this.isLoadingLabels = false;
          return res;
        });
      },
      getLabels({ id }) {
        const query = { id };

        this.isLoadingLabels = true;
        return this.$store.dispatch('threads/getLabels', query).then((res) => {
          const { labels } = this.$store.getters['threads/activeItem'];
          this.selectedLabels = labels;
          this.isLoadingLabels = false;
          return res;
        });
      },
      clear() {
        this.selectedLabels = [];
        this.labelSearch = '';
        this.isRefreshingLabels = true;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='threads-form'][data-component='labels'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  .v-chip {
    background-color: map.get($colors, primary) !important;
    color: map.get($colors, shades, 'white') !important;
    font-weight: bold;
    opacity: 1 !important;

    .v-icon {
      color: map.get($colors, shades, 'white') !important;
    }
  }
}
</style>
